/**
 * Everything dashboard-related inside a study room: the eleven live panels on the arc, table
 * pins (shared clones), broadcast, the tooltip, notices, the filter editor, panel
 * manipulation, and the query loop (form, spatial markers, query card).
 * Owned by RoomScene for the lifetime of a visit.
 *
 * Since 2026-10-06 the room is one shared view (`RoomSync`): the room filter, every screen's
 * path and filters, where the screens stand, the centre screen, the patients out for review,
 * the ward panels and the ward's overlays are the same for everyone in the room.
 */
import * as THREE from 'three';
import {
  TABLE_PIN_SLOTS,
  QUERY_CATEGORIES,
  canModifyQueries,
  type DashboardFilters,
  type DashboardId,
  type PinnedPanel,
  type Query,
  type QueryStatus,
  type RecordRef,
  type StudyFile,
} from '@clinspace/shared';
import { ApiError, api, sendJson } from '../lib/api.ts';
import { QueryCard } from '../queries/QueryCard.ts';
import { QueryForm, defaultCategory, draftToInput } from '../queries/QueryForm.ts';
import { QueryMarkers, type MarkerFrame } from '../queries/QueryMarkers.ts';
import { Annotations } from '../collab/Annotations.ts';
import { Beams } from '../collab/Beams.ts';
import { Whiteboard } from '../collab/Whiteboard.ts';
import { Presentation } from '../collab/Presentation.ts';
import { ScreenShare, WALL } from '../collab/ScreenShare.ts';
import { ModelShelf } from '../collab/ModelShelf.ts';
import type { AssistantInfo, AssistantSuggestion } from '../assistant/types.ts';
import { assistantInfo } from '../assistant/info.ts';
import { CentreScreen } from './CentreScreen.ts';
import type { SummaryRoom } from './StudySummary.ts';
import { Host } from '../assistant/Host.ts';
import { HOST_NAME } from '../assistant/tour.ts';
import { RoomFilters } from './RoomFilters.ts';
import { dockBeside, snapCandidate } from './compare.ts';
import { StageLayer, type StageItem } from './StageLayer.ts';
import { buildStage } from './stages.ts';
import type { Dashboard } from './Dashboard.ts';
import { type DashboardHost, type RecordDetail, type WardAction } from './Dashboard.ts';
import { FilterEditor } from './FilterEditor.ts';
import { PanelManipulator } from './PanelManipulator.ts';
import { StudyView } from './StudyView.ts';
import { PatientProfilesDashboard } from './impl/PatientProfiles.ts';
import { Ward, type WardTarget } from '../ward/Ward.ts';
import { REVIEW, inWardArea } from '../ward/layout.ts';
import { Tooltip } from './Tooltip.ts';
import { createDashboard, createDashboards } from './index.ts';
import type { AppContext } from '../scenes/Scene.ts';
import { ROOM, type ArcPanel, type StudyRoomWorld } from '../world/StudyRoom.ts';
import { CENTRE_STAGE, TABLE_STAGE } from '../world/plan.ts';
import { RoomSync, round3 } from './RoomSync.ts';
import { SpeechTranscriber } from '../audio/SpeechTranscriber.ts';
import { cleanFilters } from './Dashboard.ts';
import type { OverlayMode, ReviewAnchor } from '../ward/Ward.ts';

/** Where a screen stands, as the room shares it: at home, or a pose. */
interface Place {
  home?: boolean;
  p?: [number, number, number];
  q?: [number, number, number, number];
  s?: number;
  /** The screen docked beside it for comparison (its sync key). */
  compare?: string;
}

/** A profile standing in the ward, as the room shares it. */
interface WardPanelState {
  subject: string;
  path: string[];
  home: Place;
  at: Place;
  compare?: string;
}

export interface RaiseRequest {
  ref: RecordRef;
  field: string | undefined;
  dashboard: DashboardId;
  title: string;
  currentValue: string | undefined;
  origin: THREE.Vector3;
}

const PIN_SCALE = 0.45;
const PIN_RADIUS = 1.08;
const PIN_HEIGHT = 1.34;

export class DashboardRoom {
  readonly group = new THREE.Group();
  readonly view: StudyView;
  readonly dashboards: Dashboard[];
  readonly pins = new Map<string, Dashboard>();
  readonly tooltip = new Tooltip();
  readonly notice = new Tooltip();
  readonly manipulator: PanelManipulator;
  readonly filterEditor: FilterEditor;
  private noticeUntil = 0;
  private readonly lightFlash = new Map<DashboardId, number>();
  private lightPulse = 0;
  private readonly unsubscribe: (() => void)[] = [];
  private readonly host: DashboardHost;
  readonly stages: StageLayer;
  /** The projected dashboard's stage, larger in front of the centre screen and tiny on the table. */
  readonly centreStage: StageLayer;
  readonly tableStage: StageLayer;
  private mirrorBuilt = '';
  private readonly stageBuilt = new Map<DashboardId, number>();
  readonly roomFilters: RoomFilters;
  /** The extra-large screen facing the table: the study summary or a projected dashboard. */
  readonly centre: CentreScreen;
  readonly annotationLayer: Annotations;
  readonly beams: Beams;
  readonly ward: Ward;
  readonly whiteboard: Whiteboard;
  readonly presentation: Presentation;
  readonly share: ScreenShare;
  readonly shelf: ModelShelf;
  /** The host behind the reception counter: welcome, tour and spoken conversation. */
  readonly reception: Host;

  assistantInfo: AssistantInfo | undefined;
  private disposed = false;
  private readonly panels: ArcPanel[];
  /** Profile panels standing in the ward beside their mannequins (at most two). */
  readonly wardPanels: Dashboard[] = [];
  /** The profile waiting for a second mannequin to compare with. */
  private comparePick: Dashboard | undefined;
  lastWardAction: string | undefined;
  annotating = false;
  pointing = false;
  followId: string | undefined;
  private followAnchor: THREE.Vector3 | undefined;
  lastSnap: { moved: DashboardId; anchor: DashboardId } | undefined;
  readonly form: QueryForm;
  readonly card: QueryCard;
  readonly markers: QueryMarkers;
  private readonly frames: MarkerFrame[];
  /** Dev hooks for the smoke test. */
  lastRaise: RaiseRequest | undefined;
  lastSubmitted: Query | undefined;
  lastQueryEvent:
    | {
        id: string;
        event: 'created' | 'updated';
        status: QueryStatus;
        responses: number;
        receivedAt: number;
      }
    | undefined;
  lastBroadcast: { from: string; dashboard: DashboardId; path: string[] } | undefined;
  /** The last change someone else made to the room (dev hooks). */
  lastShared: { key: string; from: string } | undefined;
  /** Everything the room shares (see the file comment). */
  readonly sync: RoomSync;
  /** This visitor's voice chat for the meeting transcript, when the server writes it down. */
  transcriber: SpeechTranscriber | undefined;
  /** Screens easing toward where someone else put them. */
  private readonly targets = new Map<
    Dashboard,
    { p: THREE.Vector3; q: THREE.Quaternion; s: number }
  >();

  constructor(
    private readonly ctx: AppContext,
    file: StudyFile,
    private readonly world: StudyRoomWorld,
    liveQueries: Query[],
    /** The three character code of this room of the study. */
    readonly roomCode = '',
  ) {
    const panels = world.panels;
    this.panels = panels;
    this.view = new StudyView(file);
    this.sync = new RoomSync(ctx.net);
    for (const q of liveQueries) this.view.upsertQuery(q);
    this.manipulator = new PanelManipulator(ctx.input, {
      onRelease: (d) => this.snapCompare(d),
      onHome: (d) => this.uncompare(d),
    });
    this.host = {
      view: this.view,
      interactive: ctx.interactive,
      filters: {},
      canRaiseQueries: canModifyQueries(ctx.profile.role),
      raiseQuery: (detail, field, dashboard, origin) =>
        this.raiseQuery(detail, field, dashboard, origin),
      pin: (dashboard) => this.togglePin(dashboard),
      broadcast: (dashboard) => this.broadcast(dashboard),
      tooltip: (lines, at) => this.showTooltip(lines, at),
      editFilter: (dashboard, key, origin) =>
        this.filterEditor.show(dashboard, key, origin, ctx.rig.headPosition(new THREE.Vector3())),
      annotating: () => this.annotating,
      annotate: (dashboard, label, at) => this.addAnnotation(label, at, dashboard.id),
      ward: {
        action: (dashboard, action, subjectId, origin) =>
          this.wardAction(dashboard, action, subjectId, origin),
        overlayLabel: () => this.ward.overlayLabel(),
        timelineOn: (subjectId) => this.ward.timeline === subjectId,
        pulled: (subjectId) => this.ward.isPulled(subjectId),
        onTable: (subjectId) => !!this.profilePin(subjectId),
        filtered: (subjectId) => !!this.roomFilters.filters.subjectIds?.includes(subjectId),
        inWard: (dashboard) => this.wardPanels.includes(dashboard),
      },
    };
    this.filterEditor = new FilterEditor(this.view, ctx.interactive);
    this.group.add(this.filterEditor.panel.mesh, this.tooltip.sprite);
    this.notice.sprite.scale.set(0.42, 0.13, 1);
    this.notice.sprite.center.set(0.5, 0.5);
    this.notice.sprite.position.set(0, -0.12, -0.7);
    this.notice.sprite.visible = false;
    ctx.rig.camera.add(this.notice.sprite);

    this.dashboards = createDashboards(this.host);
    this.dashboards.forEach((d, i) => {
      const frame = panels[i];
      if (!frame) return;
      d.home.position.copy(frame.position);
      d.home.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), frame.yaw);
      d.home.scale = 1;
      d.goHome();
      // The live panel sits a hair in front of the placeholder face, which is hidden.
      d.group.translateZ(0.02);
      d.home.position.copy(d.group.position);
      frame.face.visible = false;
      frame.sign.visible = false;
      this.group.add(d.group);
      d.attach();
      this.manipulator.add(d);
    });

    this.centre = new CentreScreen(this.host, world.centreScreen, (view) => {
      this.ctx.audio.click();
      if (view !== 'summary') this.showNotice(['On the centre screen', this.centre.title], 2000);
    });
    this.group.add(this.centre.group);

    // Query loop: the form, the card and the markers.
    const sounds = {
      key: () => ctx.audio.key(),
      click: () => ctx.audio.click(),
      typing: (on: boolean) => {
        ctx.input.captureText = on;
        ctx.locomotion.enabled = !on || ctx.inXr();
      },
    };
    this.form = new QueryForm(
      ctx.interactive,
      {
        submit: (input) => this.submitQuery(input),
        cancel: () => this.showNotice(['Query discarded'], 1500),
      },
      sounds,
    );
    this.form.buildInput = () => {
      const draft = this.form.draft!;
      const subject = this.view.subject(draft.ref.subjectId);
      return draftToInput(
        draft,
        { name: ctx.profile.name, role: ctx.profile.role, sessionId: ctx.net.sessionId ?? 'local' },
        subject?.siteId ?? '',
        this.form.worldPosition(),
      );
    };
    this.card = new QueryCard(
      ctx.interactive,
      () => ctx.profile.role,
      {
        respond: (q, text) =>
          sendJson<Query>('POST', api('queries', q.id, 'responses'), {
            by: ctx.profile.name,
            role: ctx.profile.role,
            text,
          }).catch(rethrowApi),
        setStatus: (q, status) =>
          sendJson<Query>('PATCH', api('queries', q.id), {
            status,
            by: ctx.profile.name,
            role: ctx.profile.role,
          }).catch(rethrowApi),
        showRecord: (q) => this.showQueryRecord(q),
      },
      sounds,
    );
    this.frames = panels.map((p) => ({
      dashboard: p.id,
      position: { x: p.position.x, y: p.position.y, z: p.position.z },
      yaw: p.yaw,
      width: ROOM.panelWidth,
      height: ROOM.panelHeight,
    }));
    this.markers = new QueryMarkers(ctx.interactive, {
      tooltip: (id, at) => this.showTooltip(id ? this.markerTooltip(id) : undefined, at),
      select: (id, at) => this.openQueryCard(id, at),
    });
    this.group.add(this.form.group, this.card.group, this.markers.mesh);
    this.markers.set(this.view.queries, this.frames);

    // The always-on stages on the floor in front of every frame.
    this.stages = new StageLayer(
      panels.map((p) => ({
        dashboard: p.id,
        origin: { x: p.position.x, z: p.position.z },
        yaw: p.yaw,
      })),
      ctx.interactive,
      {
        tooltip: (lines, at) => this.showTooltip(lines, at),
        select: (id, item, at) => this.stageSelect(id, item, at),
      },
    );
    this.group.add(this.stages.group);

    // Copies of the projected dashboard's stage: large on the floor in front of the centre
    // screen, a miniature over the table beyond the console. Both act on the centre screen.
    const centreFloor = world.centreScreen.position;
    const mirrorHandlers = {
      tooltip: (lines: string[] | undefined, at: THREE.Vector3 | undefined) =>
        this.showTooltip(lines, at),
      select: (_id: DashboardId, item: StageItem, at: THREE.Vector3) => this.mirrorSelect(item, at),
    };
    this.centreStage = new StageLayer(
      [
        {
          dashboard: 'Enrollment',
          origin: { x: centreFloor.x, z: centreFloor.z },
          yaw: world.centreScreen.yaw,
          scale: CENTRE_STAGE.scale,
          labels: false,
        },
      ],
      ctx.interactive,
      mirrorHandlers,
    );
    this.tableStage = new StageLayer(
      [
        {
          dashboard: 'Enrollment',
          origin: TABLE_STAGE.origin,
          yaw: 0,
          scale: TABLE_STAGE.scale,
          y: TABLE_STAGE.y,
          labels: false,
        },
      ],
      ctx.interactive,
      mirrorHandlers,
    );
    this.centreStage.retarget(undefined);
    this.tableStage.retarget(undefined);
    this.centreStage.group.name = 'centre-stage';
    this.tableStage.group.name = 'table-stage';
    this.group.add(this.centreStage.group, this.tableStage.group);

    // Spatial collaboration: physical filters, annotations, beams.
    this.roomFilters = new RoomFilters(this.view, ctx.interactive, {
      apply: (patch) => this.applyRoomFilter(patch),
      tooltip: (lines, at) => this.showTooltip(lines, at),
      resetScreens: () => this.resetScreens(),
      screens: () => [
        {
          id: 'summary' as const,
          title: 'Study summary',
          canvas: this.centre.summary.panel.canvas,
          version: this.centre.summary.version,
        },
        ...this.dashboards.map((d) => ({
          id: d.id,
          title: d.title,
          canvas: d.panel.canvas,
          version: d.stageVersion,
        })),
      ],
      onScreen: () => this.centre.showing,
      project: (view) => this.centre.show(view),
    });
    const visit = this.dashboard('VisitCompliance');
    const qm = this.dashboard('QueryManagement');
    if (visit && qm) this.roomFilters.attachRails(visit.group, qm.group);
    this.annotationLayer = new Annotations(ctx.interactive, {
      tooltip: (lines, at) => this.showTooltip(lines, at),
      select: (a) => ctx.net.removeAnnotation(a.id),
    });
    this.beams = new Beams(ctx.input, (origin, target) => ctx.net.sendBeam(origin, target));
    this.group.add(this.roomFilters.group, this.annotationLayer.mesh, this.beams.group);
    this.annotationLayer.set(ctx.net.annotations);

    // The patient ward beyond the arc: every subject as a mannequin.
    this.ward = new Ward(this.view, ctx.interactive, ctx.input, ctx.avatars, {
      tooltip: (lines, at) => this.showTooltip(lines, at),
      select: (target, at) => this.wardSelect(target, at),
      grab: (subjectId) => this.toggleReview(subjectId),
      sendBackAll: () => this.sendBack(),
      reviewMoved: (subjectId) => this.followReview(subjectId),
      filter: (patch) => this.applyRoomFilter(patch),
      timelineStep: (subjectId, visitId) => this.timelineStep(subjectId, visitId),
    });
    this.group.add(this.ward.group);

    // The shared whiteboard in the studio.
    this.whiteboard = new Whiteboard(
      ctx.net,
      ctx.interactive,
      ctx.input,
      {
        tooltip: (lines, at) => this.showTooltip(lines, at),
        notice: (lines, ms) => this.showNotice(lines, ms),
        head: () => ctx.rig.headPosition(new THREE.Vector3()),
        name: () => ctx.profile.name,
        study: () => this.view.code,
      },
      sounds,
    );
    this.group.add(this.whiteboard.group);

    // The presentation wall, the lectern and the deck.
    this.share = new ScreenShare(ctx.net, ctx.voice, {
      notice: (lines, ms) => this.showNotice(lines, ms),
      inXr: () => ctx.inXr(),
      name: () => ctx.profile.name,
    });
    this.presentation = new Presentation(ctx.net, this.host, ctx.interactive, ctx.rig.camera, {
      tooltip: (lines, at) => this.showTooltip(lines, at),
      notice: (lines, ms) => this.showNotice(lines, ms),
      goToAudience: (x, z) => {
        ctx.locomotion.teleport(new THREE.Vector3(x, 0, z));
        ctx.rig.faceTowards(new THREE.Vector3(x, 1.6, WALL.z), ctx.inXr());
      },
      togglePoint: () => this.togglePoint(),
      pointing: () => this.pointing,
      pins: () => ctx.net.pins,
      nonSpatial: (id, on) => ctx.voice.setNonSpatial(id, on),
      spotlight: (on) => this.share.setSpotlight(on),
    });
    this.group.add(this.share.group, this.presentation.group);

    // The model shelf by the entrance and the models on the table.
    this.shelf = new ModelShelf(this.view, ctx.net, ctx.interactive, ctx.input, {
      tooltip: (lines, at) => this.showTooltip(lines, at),
      notice: (lines, ms) => this.showNotice(lines, ms),
      head: () => ctx.rig.headPosition(new THREE.Vector3()),
      feet: () => ctx.rig.feetPosition(new THREE.Vector3()),
    });
    this.group.add(this.shelf.group);

    // Luma at the reception: welcome, tour, and every conversation with the assistant (her
    // card took over the AI desk's console and answer card).
    this.reception = new Host({
      interactive: ctx.interactive,
      loader: ctx.avatars,
      study: { code: this.view.code },
      visitor: () => ctx.profile.name,
      head: () => ctx.rig.headPosition(new THREE.Vector3()),
      facing: () => ctx.rig.camera.getWorldDirection(new THREE.Vector3()),
      teleport: (x, z, look) => {
        ctx.locomotion.teleport(new THREE.Vector3(x, 0, z));
        ctx.rig.faceTowards(look, ctx.inXr());
      },
      highlight: (id) => this.highlightDashboard(id, `${HOST_NAME} points at`),
      raise: (s, origin) => this.raiseSuggested(s, origin),
      click: () => ctx.audio.click(),
      info: () => this.assistantInfo,
      inXr: () => ctx.inXr(),
      audioContext: () => ctx.audio.context,
      sounds,
      net: ctx.net,
      sync: this.sync,
      microphone: () => ctx.voice.microphone?.stream,
    });
    this.group.add(this.reception.group);
    void assistantInfo()
      .then((info) => {
        this.assistantInfo = info;
        if (this.disposed) return;
        this.reception.setInfo(info);
        if (info.transcripts) this.startTranscript();
        if (!info.enabled) return;
        this.form.suggest = (draft) =>
          sendJson<{ suggestion?: AssistantSuggestion }>('POST', api('assistant', 'suggest'), {
            study: this.view.code,
            ref: {
              subjectId: draft.ref.subjectId,
              form: draft.ref.form,
              recordId: draft.ref.recordId,
              visit: draft.ref.visit,
              field: draft.ref.field,
            },
            currentValue: draft.currentValue,
          }).then((r) => r.suggestion?.text);
      })
      .catch((err) => console.warn('[assistant] unavailable:', (err as Error).message));

    const net = ctx.net;
    this.unsubscribe.push(
      net.onPins((pins) => this.setPins(pins)),
      net.onPinAdded((pin) => this.addPin(pin)),
      net.onPinRemoved((id) => this.removePin(id)),
      net.onBroadcast((from, dashboard, path) => this.onBroadcast(from.name, dashboard, path)),
      net.onQuery((q, event) => this.onQuery(q, event)),
      net.onAnnotations((items) => this.annotationLayer.set(items)),
      net.onBeam((from, origin, target) => this.beams.onBeam(from, origin, target)),
      net.onPresenter((state) => this.presentation.onState(state)),
      net.onRecentre((from) => this.presentation.onRecentre(from)),
      net.onShare((id, name, on) => this.share.onShareState(id, name, on)),
      net.onError((code, message) => {
        if (code === 'not_allowed') this.showNotice(['Not allowed', message], 3000);
      }),
    );
    if (net.pins.length) this.setPins(net.pins);
    if (net.sharer) this.share.onShareState(net.sharer.sessionId, net.sharer.name, true);
    for (const [id, stream] of ctx.remoteShares()) this.share.onRemote(id, stream);
    if (net.presenter) this.presentation.onState(net.presenter);
    this.shareRoom();
  }

  /** Voice chat into the meeting transcript; everyone is told on entering. */
  private startTranscript(): void {
    const { ctx } = this;
    this.transcriber = new SpeechTranscriber({
      microphone: () => ctx.voice.microphone,
      sessionId: () => ctx.net.sessionId,
      paused: () =>
        ctx.voice.isMuted ||
        ctx.net.snapshotRoom !== ctx.net.currentRoom ||
        this.reception.phase === 'listening' ||
        this.reception.speaking,
    });
    this.showNotice(
      [
        'This meeting is transcribed',
        'Voice chat and questions to Luma go into the meeting transcript.',
      ],
      4500,
    );
  }

  /* ---------- The shared room (2026-10-06) ---------- */

  /**
   * Registers what the room shares. Each channel applies the room's current value at once, in
   * this order: the room filter first, then the screens that it filters.
   */
  private shareRoom(): void {
    const s = this.sync;
    s.register('filters', {
      read: () => cleanFilters(this.roomFilters.filters),
      apply: (v, from) => this.applySharedFilters(v as DashboardFilters, from.name),
    });
    for (const d of this.dashboards) this.shareScreen(`dash:${d.id}`, d);
    s.register('centre', {
      read: () => this.centre.state(),
      apply: (v, from) => {
        if (this.centre.setState(v as Record<string, unknown>))
          this.noteShared('centre', from.name);
      },
    });
    s.register('review', {
      read: () => this.ward.reviewAnchors(),
      apply: (v, from) => {
        const list = Array.isArray(v) ? (v as ReviewAnchor[]) : [];
        const { out, back } = this.ward.setReviews(list);
        if (out.length || back.length) {
          this.noteShared('review', from.name);
          this.showNotice(
            [
              out.length
                ? `${from.name || 'Someone'} summoned ${out.join(', ')} for review`
                : `${from.name || 'Someone'} sent ${back.join(', ')} back`,
              'The cockpit stands where they put it.',
            ],
            3000,
          );
        }
      },
    });
    s.register('ward', {
      read: () => ({ overlays: this.ward.overlays, timeline: this.ward.timeline ?? null }),
      apply: (v) => {
        const w = v as { overlays?: OverlayMode; timeline?: string | null };
        if (w.overlays) this.ward.setOverlays(w.overlays);
        this.ward.setTimeline(w.timeline ?? undefined);
        for (const d of [...this.dashboards, ...this.pins.values(), ...this.wardPanels])
          if (d.id === 'PatientProfile') d.panel.invalidate();
      },
    });
    s.register('wardPanels', {
      read: () => this.wardPanels.map((d) => this.wardPanelState(d)),
      apply: (v) => this.applyWardPanels(Array.isArray(v) ? (v as WardPanelState[]) : []),
    });
  }

  /** A screen's view and where it stands, under one key each. */
  private shareScreen(key: string, d: Dashboard): void {
    this.sync.register(key, {
      read: () => d.viewState(),
      apply: (v, from) => {
        if (!d.setView(v as Record<string, unknown>)) return;
        this.noteShared(key, from.name);
        if (!d.pinId) this.lightFlash.set(d.id, performance.now() + 1200);
      },
    });
    this.sync.register(`place:${key}`, {
      read: () => this.placeOf(d),
      apply: (v) => this.applyPlace(d, v as Place),
    });
  }

  private noteShared(key: string, from: string): void {
    this.lastShared = { key, from };
  }

  /** The sync key of a screen (to name what it is docked beside). */
  private keyOf(d: Dashboard): string | undefined {
    if (d.pinId) return `pin:${d.pinId}`;
    if (this.wardPanels.includes(d)) {
      const subject = (d as PatientProfilesDashboard).currentSubject();
      return subject ? `ward:${subject}` : undefined;
    }
    return this.dashboards.includes(d) ? `dash:${d.id}` : undefined;
  }

  private byKey(key: string): Dashboard | undefined {
    if (key.startsWith('pin:')) return this.pins.get(key.slice(4));
    if (key.startsWith('ward:'))
      return this.wardPanels.find(
        (d) => (d as PatientProfilesDashboard).currentSubject() === key.slice(5),
      );
    if (key.startsWith('dash:')) return this.dashboard(key.slice(5) as DashboardId);
    return undefined;
  }

  private placeOf(d: Dashboard): Place {
    const t = this.targets.get(d);
    const p = t?.p ?? d.group.position;
    const q = t?.q ?? d.group.quaternion;
    const s = t?.s ?? d.group.scale.x;
    const compare = d.compareTo ? this.keyOf(d.compareTo) : undefined;
    const atHome =
      p.distanceTo(d.home.position) < 0.002 &&
      Math.abs(q.angleTo(d.home.quaternion)) < 0.002 &&
      Math.abs(s - d.home.scale) < 0.002;
    const extra = compare ? { compare } : {};
    if (atHome) return { home: true, ...extra };
    return { ...poseOf(p, q, s), ...extra };
  }

  /** Someone moved a screen: it eases there (unless this user is holding it). */
  private applyPlace(d: Dashboard, v: Place): void {
    if (this.manipulator.isHeld(d)) return;
    const target = v.home
      ? { p: d.home.position.clone(), q: d.home.quaternion.clone(), s: d.home.scale }
      : v.p && v.q && typeof v.s === 'number'
        ? {
            p: new THREE.Vector3(...v.p),
            q: new THREE.Quaternion(...v.q).normalize(),
            s: THREE.MathUtils.clamp(v.s, 0.1, 3),
          }
        : undefined;
    if (target) {
      if (d.group.position.distanceTo(target.p) < 0.01) {
        d.group.position.copy(target.p);
        d.group.quaternion.copy(target.q);
        d.group.scale.setScalar(target.s);
        this.targets.delete(d);
      } else this.targets.set(d, target);
    }
    const other = v.compare ? this.byKey(v.compare) : undefined;
    if (other !== d.compareTo) {
      if (d.compareTo) this.uncompare(d);
      if (other && other !== d) {
        if (other.compareTo && other.compareTo !== d) this.uncompare(other);
        d.compareTo = other;
        other.compareTo = d;
        d.panel.invalidate();
        other.panel.invalidate();
      }
    }
  }

  /** Screens move toward where someone else put them, over a fraction of a second. */
  private easeScreens(dt: number): void {
    if (!this.targets.size) return;
    const k = 1 - Math.exp(-dt * 14);
    for (const [d, t] of this.targets) {
      if (this.manipulator.isHeld(d) || !d.group.parent) {
        this.targets.delete(d);
        continue;
      }
      d.group.position.lerp(t.p, k);
      d.group.quaternion.slerp(t.q, k);
      d.group.scale.setScalar(THREE.MathUtils.lerp(d.group.scale.x, t.s, k));
      if (d.group.position.distanceTo(t.p) < 0.002 && Math.abs(d.group.scale.x - t.s) < 0.002) {
        d.group.position.copy(t.p);
        d.group.quaternion.copy(t.q);
        d.group.scale.setScalar(t.s);
        this.targets.delete(d);
      }
    }
  }

  private applySharedFilters(next: DashboardFilters, from: string): void {
    const before = JSON.stringify(cleanFilters(this.roomFilters.filters));
    const filters = cleanFilters(next);
    if (JSON.stringify(filters) === before) return;
    this.roomFilters.setFilters(filters);
    this.ward.setFilters(filters);
    this.centre.setFilters(filters);
    this.noteShared('filters', from);
    this.showNotice(
      [`${from || 'Someone'} filtered the room`, describeFilters(filters, this.view)],
      2500,
    );
  }

  private wardPanelState(d: Dashboard): WardPanelState {
    const subject = (d as PatientProfilesDashboard).currentSubject() ?? '';
    const t = this.targets.get(d);
    const compare = d.compareTo ? this.keyOf(d.compareTo) : undefined;
    return {
      subject,
      path: [...d.path],
      home: poseOf(d.home.position, d.home.quaternion, d.home.scale),
      at: poseOf(t?.p ?? d.group.position, t?.q ?? d.group.quaternion, t?.s ?? d.group.scale.x),
      ...(compare ? { compare } : {}),
    };
  }

  /** Someone else's ward panels: open, re-point, move and close to match. */
  private applyWardPanels(list: WardPanelState[]): void {
    const wanted = new Set(list.map((w) => w.subject));
    for (const d of [...this.wardPanels]) {
      const subject = (d as PatientProfilesDashboard).currentSubject();
      if (!subject || !wanted.has(subject)) this.closeWardPanel(d);
    }
    for (const w of list.slice(0, 6)) {
      if (!w.subject || !w.home?.p || !w.home.q) continue;
      let d = this.wardPanels.find(
        (x) => (x as PatientProfilesDashboard).currentSubject() === w.subject,
      );
      const fresh = !d;
      if (!d) {
        d = createDashboard('PatientProfile', this.host);
        d.path = [...w.path];
        d.invalidate();
      } else if (JSON.stringify(d.path) !== JSON.stringify(w.path)) d.setPath(w.path);
      d.home.position.set(...w.home.p);
      d.home.quaternion.set(...w.home.q).normalize();
      d.home.scale = w.home.s ?? 1;
      if (fresh) {
        // A new panel appears where it stands for the others, not easing in from elsewhere.
        d.goHome();
        if (w.at?.p && w.at.q) {
          d.group.position.set(...w.at.p);
          d.group.quaternion.set(...w.at.q).normalize();
          d.group.scale.setScalar(w.at.s ?? d.home.scale);
        }
        this.group.add(d.group);
        d.attach();
        this.manipulator.add(d);
        this.wardPanels.push(d);
      } else if (w.at?.p && w.at.q) this.applyPlace(d, { ...w.at, home: false });
    }
    for (const w of list) {
      const d = this.byKey(`ward:${w.subject}`);
      const other = w.compare ? this.byKey(w.compare) : undefined;
      if (d && other && d.compareTo !== other) {
        d.compareTo = other;
        other.compareTo = d;
        d.panel.invalidate();
        other.panel.invalidate();
      }
    }
    this.ward.setOpen(this.wardSubjects());
  }

  /** Wrist menu "Pull screen": a clone of the wall display in front of the viewer. */
  pullScreen(): void {
    const head = this.ctx.rig.headPosition(new THREE.Vector3());
    const forward = this.ctx.rig.headForward(new THREE.Vector3());
    const shown = this.share.togglePull(head, forward);
    this.showNotice([shown ? 'Screen clone in front of you' : 'Screen clone put away'], 1500);
  }

  /** The room's code, the people in it and the transcript note, on the centre screen's summary. */
  setRoomInfo(room: SummaryRoom): void {
    this.centre.summary.setRoom(room);
  }

  dispose(): void {
    this.transcriber?.dispose();
    this.sync.dispose();
    for (const u of this.unsubscribe) u();
    for (const d of this.dashboards) d.dispose();
    for (const d of this.wardPanels) d.dispose();
    this.wardPanels.length = 0;
    this.ward.dispose();
    this.whiteboard.dispose();
    this.presentation.dispose();
    this.share.dispose();
    this.shelf.dispose();
    this.disposed = true;
    this.reception.dispose();
    for (const d of this.pins.values()) d.dispose();
    this.pins.clear();
    this.filterEditor.hide();
    this.filterEditor.panel.dispose();
    this.manipulator.dispose();
    this.form.dispose();
    this.card.dispose();
    this.markers.dispose();
    this.roomFilters.dispose();
    this.centre.dispose();
    this.stages.dispose();
    this.centreStage.dispose();
    this.tableStage.dispose();
    this.annotationLayer.dispose();
    this.beams.dispose();
    this.ctx.locomotion.enabled = true;
    this.ctx.input.captureText = false;
    this.ctx.rig.camera.remove(this.notice.sprite);
    for (const p of this.panels) {
      p.face.visible = true;
      p.sign.visible = true;
    }
  }

  dashboard(id: DashboardId): Dashboard | undefined {
    return this.dashboards.find((d) => d.id === id);
  }

  update(dt: number): void {
    this.manipulator.update();
    this.easeScreens(dt);
    for (const d of this.dashboards) d.update();
    for (const d of this.pins.values()) d.update();
    // A ward panel backed out of its subject closes.
    for (const d of [...this.wardPanels]) {
      if (d.path.length === 0) this.closeWardPanel(d);
      else d.update();
    }
    const head = this.ctx.rig.headPosition(new THREE.Vector3());
    const feet = this.ctx.rig.feetPosition(new THREE.Vector3());
    this.ward.update(dt, head, feet);
    // Inside the ward the arc stages are behind the viewer and out of reach: skipping their
    // draw calls there keeps the ward (the heaviest view) nearer the budget.
    const inWard = inWardArea(feet, this.world.wardRoom) && this.ward.group.visible;
    const stagesWanted = !inWard;
    if (this.stages.group.visible !== stagesWanted) this.stages.group.visible = stagesWanted;
    this.filterEditor.update();
    this.form.update();
    this.card.update();
    this.centre.update();
    this.roomFilters.update();
    for (const d of this.dashboards) {
      if (this.stageBuilt.get(d.id) !== d.stageVersion) {
        this.stageBuilt.set(d.id, d.stageVersion);
        this.stages.set(d.id, buildStage(d));
      }
    }
    this.stages.update();
    this.updateMirrorStages(stagesWanted, inWard);
    this.beams.update(this.pointing);
    this.whiteboard.update();
    this.presentation.update(head);
    this.shelf.update(dt);
    // The host greets a visitor who comes in, once, as soon as the room's state has arrived
    // (she may be busy with someone else's tour) and her voice is known; until 2026-10-06
    // it also waited a fixed 1.5 s. Her body, the room's data and the welcome's audio are
    // fetched in the lobby while the room code is typed (LobbyScene.enterRoom).
    if (
      !this.reception.welcomed &&
      this.assistantInfo &&
      this.reception.avatar.loaded &&
      this.ctx.net.snapshotRoom === this.ctx.net.currentRoom &&
      this.reception.inWelcomeRange(head)
    )
      this.reception.welcome();
    this.reception.update(dt);
    this.updateFollow();
    // After everything has had its say this frame: share what changed.
    this.sync.update();
    this.transcriber?.update();
    // Typing on desktop must not walk the user around.
    const typing =
      this.form.typing || this.card.typing || this.whiteboard.typing || this.reception.card.typing;
    // On desktop the physical keyboard both types and walks (WASD), so walking pauses while a
    // field is active. In VR the world keyboard never moves anyone: keep teleport, turning and
    // the thumbsticks working, so nobody is stuck in front of a keyboard.
    this.ctx.locomotion.enabled = !typing || this.ctx.inXr();
    this.ctx.input.captureText = typing;
    const now = performance.now();
    if (this.notice.sprite.visible && now > this.noticeUntil) this.notice.sprite.visible = false;
    // Status lights: a slow pulse, or a fast strobe for a few seconds after a broadcast.
    this.lightPulse += dt;
    const pulse = 0.9 + Math.sin(this.lightPulse * 2) * 0.3;
    for (const p of this.panels) {
      const until = this.lightFlash.get(p.id);
      const material = p.statusLight.material as THREE.MeshStandardMaterial;
      if (until && now < until)
        material.emissiveIntensity = 1.5 + Math.sin(this.lightPulse * 18) * 1.4;
      else {
        if (until) this.lightFlash.delete(p.id);
        material.emissiveIntensity = pulse;
      }
    }
  }

  /** Physical keyboard on desktop: the open form or card gets it first. */
  handleKeyEvent(e: KeyboardEvent): boolean {
    if (this.form.isOpen) return this.form.handleKeyEvent(e);
    if (this.card.isOpen) return this.card.handleKeyEvent(e);
    if (this.whiteboard.editor.isOpen) return this.whiteboard.handleKeyEvent(e);
    if (this.reception.card.typing) return this.reception.card.handleKeyEvent(e);
    return false;
  }

  /* ---------- Spatial collaboration ---------- */

  /** Applies a filter patch to every dashboard and the physical filter widgets. */
  applyRoomFilter(patch: Partial<DashboardFilters>): void {
    const next = { ...this.roomFilters.filters, ...patch };
    for (const k of Object.keys(next) as (keyof DashboardFilters)[])
      if (next[k] === undefined) delete next[k];
    if (next.subjectIds && !next.subjectIds.length) delete next.subjectIds;
    // Patients out for review are not a filter (2026-10-06): filters never send them back.
    this.roomFilters.setFilters(next);
    for (const d of this.dashboards) d.setFilters(next);
    this.centre.setFilters(next);
    this.ward.setFilters(next);
    this.showNotice(['Room filter', describeFilters(next, this.view)], 2500);
    this.ctx.audio.click();
  }

  private snapCompare(released: Dashboard): void {
    const others = [...this.dashboards, ...this.pins.values()];
    const anchor = snapCandidate(released, others);
    if (!anchor || anchor === released.compareTo) return;
    const pose = dockBeside(
      {
        position: anchor.group.position.clone(),
        quaternion: anchor.group.quaternion.clone(),
        scale: anchor.group.scale.x,
      },
      anchor.group.scale.x,
    );
    released.group.position.copy(pose.position);
    released.group.quaternion.copy(pose.quaternion);
    released.group.scale.setScalar(pose.scale);
    if (released.compareTo) this.uncompare(released);
    if (anchor.compareTo) this.uncompare(anchor);
    released.compareTo = anchor;
    anchor.compareTo = released;
    released.panel.invalidate();
    anchor.panel.invalidate();
    this.lastSnap = { moved: released.id, anchor: anchor.id };
    this.showNotice(
      [
        `Comparing ${released.title} with ${anchor.title}`,
        released.id === anchor.id
          ? "KPI tiles show the other panel's value."
          : 'Side by side, same facing.',
      ],
      3000,
    );
    this.ctx.audio.click();
  }

  /** Dev hook: snap one panel beside another without a grab. */
  compare(movedId: DashboardId, anchorId: DashboardId): boolean {
    const moved = this.dashboard(movedId);
    const anchor = this.dashboard(anchorId);
    if (!moved || !anchor || moved === anchor) return false;
    moved.group.position.copy(anchor.group.position).add(new THREE.Vector3(0.1, 0, 0.1));
    moved.group.updateMatrixWorld(true);
    this.snapCompare(moved);
    return moved.compareTo === anchor;
  }

  private uncompare(d: Dashboard): void {
    const other = d.compareTo;
    d.compareTo = undefined;
    d.panel.invalidate();
    if (other && other.compareTo === d) {
      other.compareTo = undefined;
      other.panel.invalidate();
    }
  }

  /**
   * "Reset all screens" on the table console: every dashboard and pinned clone back to its
   * overview and its frame, open forms and cards closed. Filters are kept ("Clear filters"
   * is its own control).
   */
  resetScreens(): void {
    for (const d of [...this.dashboards, ...this.pins.values()]) {
      if (d.path.length) d.setPath([]);
      d.tidy();
    }
    for (const d of [...this.wardPanels]) this.closeWardPanel(d);
    this.centre.show('summary');
    this.ward.setTimeline(undefined);
    this.comparePick = undefined;
    if (this.ward.pulled) this.sendBack();
    this.filterEditor.hide();
    this.form.hide();
    this.card.hide();
    this.showNotice(['All screens reset', 'Every dashboard is back on its overview.'], 2500);
    this.ctx.audio.click();
  }

  /** Wrist-menu Tidy: reset all screens and leave Annotate and Point modes. */
  tidyRoom(): void {
    this.resetScreens();
    this.annotating = false;
    this.pointing = false;
  }

  toggleAnnotate(): boolean {
    this.annotating = !this.annotating;
    if (this.annotating) this.pointing = false;
    this.showNotice(
      this.annotating
        ? ['Annotate mode', 'Select anything on a dashboard to leave a note everyone sees.']
        : ['Annotate mode off'],
      2500,
    );
    return this.annotating;
  }

  addAnnotation(label: string, at: THREE.Vector3, dashboard?: DashboardId): void {
    const text = `${label}`.slice(0, 280);
    this.ctx.net.addAnnotation(
      { x: round2(at.x), y: round2(at.y), z: round2(at.z) },
      text,
      dashboard,
    );
    this.ctx.audio.click();
  }

  /** Dev hook: annotate a region of a dashboard by id. */
  annotateRegion(id: DashboardId, regionId: string): boolean {
    const d = this.dashboard(id);
    const region = d?.panel.allRegions().find((r) => r.id === regionId);
    if (!d || !region) return false;
    const at = d.panel.toWorld(region.x + region.w / 2, region.y + region.h / 2);
    this.addAnnotation(region.tooltip?.[0] ?? regionId, at, id);
    return true;
  }

  togglePoint(): boolean {
    this.pointing = !this.pointing;
    if (this.pointing) this.annotating = false;
    this.showNotice(
      this.pointing
        ? ['Pointing', 'Everyone sees a beam from your hand to what you point at.']
        : ['Pointing off'],
      2500,
    );
    return this.pointing;
  }

  /** Follows the next member in the room (or stops after the last). */
  cycleFollow(): string | undefined {
    const others = this.ctx.net.others();
    if (!others.length) {
      this.followId = undefined;
      this.showNotice(['Nobody else to follow'], 2000);
      return undefined;
    }
    const i = others.findIndex((m) => m.sessionId === this.followId);
    const next = others[i + 1];
    this.followId = next?.sessionId;
    this.followAnchor = undefined;
    this.showNotice(
      next
        ? [`Following ${next.name}`, 'You teleport behind them when they move.']
        : ['Stopped following'],
      2500,
    );
    return this.followId;
  }

  follow(sessionId: string | undefined): void {
    this.followId = sessionId;
    this.followAnchor = undefined;
  }

  followingName(): string | undefined {
    return this.ctx.net.others().find((m) => m.sessionId === this.followId)?.name;
  }

  private updateFollow(): void {
    if (!this.followId) return;
    const head = this.ctx.remoteHeads().get(this.followId);
    if (!head) return;
    if (this.followAnchor && this.followAnchor.distanceTo(head.position) < 1.5) return;
    // Stand 1.4 m behind the leader, facing where they face.
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(head.quaternion);
    forward.y = 0;
    if (forward.lengthSq() < 1e-4) forward.set(0, 0, -1);
    forward.normalize();
    const target = head.position.clone().addScaledVector(forward, -1.4);
    target.y = 0;
    // Never into the doorway: behind a leader standing just inside the entrance is the exit
    // zone, which would send the follower back to the lobby.
    target.z = Math.min(target.z, ROOM.entranceZ - 0.9);
    this.ctx.locomotion.teleport(target);
    this.ctx.rig.faceTowards(head.position.clone().addScaledVector(forward, 2), this.ctx.inXr());
    this.followAnchor = head.position.clone();
  }

  /** A stage object was selected: drill its dashboard, filter the room, or annotate. */
  /** The projected dashboard's stage follows the centre screen: its view, filters and path. */
  private updateMirrorStages(stagesWanted: boolean, inWard: boolean): void {
    const clone = this.centre.isVisible ? this.centre.clone : undefined;
    const want = clone?.id;
    if (this.centreStage.target !== want) {
      this.centreStage.retarget(want);
      this.tableStage.retarget(want);
      this.mirrorBuilt = '';
    }
    if (clone) {
      const key = `${clone.id}:${clone.stageVersion}`;
      if (key !== this.mirrorBuilt) {
        this.mirrorBuilt = key;
        const scene = buildStage(clone);
        this.centreStage.set(clone.id, scene);
        this.tableStage.set(clone.id, scene);
      }
    }
    this.centreStage.group.visible = !!clone && stagesWanted;
    this.tableStage.group.visible = !!clone && !inWard;
    this.centreStage.update();
    this.tableStage.update();
  }

  /** A selection on a copy of the stage drills the centre screen or filters the room. */
  private mirrorSelect(item: StageItem, at: THREE.Vector3): void {
    const clone = this.centre.clone;
    if (!clone) return;
    if (this.annotating) {
      this.addAnnotation(item.tooltip[0] ?? clone.title, at, clone.id);
      return;
    }
    if (!item.select) return;
    if (item.select.kind === 'drill') {
      clone.setPath(item.select.path);
      clone.flash(1500);
      this.ctx.audio.click();
    } else this.applyRoomFilter(item.select.patch);
  }

  private stageSelect(id: DashboardId, item: StageItem, at: THREE.Vector3): void {
    if (this.annotating) {
      this.addAnnotation(item.tooltip[0] ?? id, at, id);
      return;
    }
    const d = this.dashboard(id);
    if (!d || !item.select) return;
    if (item.select.kind === 'drill') {
      d.setPath(item.select.path);
      d.flash(1500);
      this.lightFlash.set(id, performance.now() + 1500);
      this.ctx.audio.click();
    } else this.applyRoomFilter(item.select.patch);
  }

  /* ---------- The ward ---------- */

  private wardSelect(target: WardTarget, at: THREE.Vector3): void {
    if (this.annotating) {
      this.addAnnotation(`${target.subjectId} (ward)`, at, 'PatientProfile');
      return;
    }
    const subject = `subject:${target.subjectId}`;
    let path: string[];
    switch (target.kind) {
      case 'subject':
        path = [subject];
        break;
      case 'visit':
        path = [subject, 'tab:Visits', `visit:${target.visitId}`];
        break;
      case 'lab':
        path = [subject, 'tab:Labs', `analyte:${target.analyte}`, `lab:${target.labId}`];
        break;
      case 'ae':
        path = [subject, 'tab:Adverse Events', `ae:${target.aeId}`];
        break;
      case 'ecg':
        path = [subject, 'tab:Vitals', `ecg:${target.ecgId}`];
        break;
      case 'record':
        path = target.path;
        break;
    }
    const compareWith = this.comparePick;
    this.comparePick = undefined;
    if (compareWith && this.wardPanels.includes(compareWith)) {
      const other = (compareWith as PatientProfilesDashboard).currentSubject();
      if (other === target.subjectId) {
        this.showNotice(['Pick a different subject to compare'], 2000);
        this.comparePick = compareWith;
        return;
      }
      this.openWardProfile(target.subjectId, path, compareWith);
    } else this.openWardProfile(target.subjectId, path);
    this.ctx.audio.click();
  }

  /** Opens (or re-points) the profile panel beside a mannequin. */
  openWardProfile(
    subjectId: string,
    path: string[],
    compareWith?: Dashboard,
  ): Dashboard | undefined {
    const head = this.ctx.rig.headPosition(new THREE.Vector3());
    const existing = this.wardPanels.find(
      (d) => (d as PatientProfilesDashboard).currentSubject() === subjectId,
    );
    if (existing) {
      existing.setPath(path);
      existing.flash(800);
      return existing;
    }
    // Profiles of summoned patients stay with their cockpits; any other ward panel makes way.
    const keep = (d: Dashboard) =>
      d === compareWith ||
      this.ward.isPulled((d as PatientProfilesDashboard).currentSubject() ?? '');
    for (const d of [...this.wardPanels]) if (!keep(d)) this.closeWardPanel(d);
    const panel = createDashboard('PatientProfile', this.host);
    panel.path = [...path];
    panel.invalidate();
    const pose = compareWith
      ? dockBeside(
          {
            position: compareWith.group.position.clone(),
            quaternion: compareWith.group.quaternion.clone(),
            scale: compareWith.group.scale.x,
          },
          compareWith.group.scale.x,
        )
      : this.ward.profilePose(subjectId, head);
    if (!pose) return undefined;
    panel.home.position.copy(pose.position);
    panel.home.quaternion.copy(pose.quaternion);
    panel.home.scale = compareWith ? compareWith.group.scale.x : pose.scale;
    panel.goHome();
    this.group.add(panel.group);
    panel.attach();
    this.manipulator.add(panel);
    this.wardPanels.push(panel);
    if (compareWith) {
      panel.compareTo = compareWith;
      compareWith.compareTo = panel;
      compareWith.panel.invalidate();
      this.lastSnap = { moved: panel.id, anchor: compareWith.id };
    }
    this.ward.setOpen(this.wardSubjects());
    return panel;
  }

  private wardSubjects(): string[] {
    return this.wardPanels
      .map((d) => (d as PatientProfilesDashboard).currentSubject())
      .filter((id): id is string => !!id);
  }

  closeWardPanel(d: Dashboard): void {
    const i = this.wardPanels.indexOf(d);
    if (i < 0) return;
    this.wardPanels.splice(i, 1);
    const subject = (d as PatientProfilesDashboard).currentSubject();
    if (d.compareTo) {
      d.compareTo.compareTo = undefined;
      d.compareTo.panel.invalidate();
    }
    if (this.comparePick === d) this.comparePick = undefined;
    this.manipulator.remove(d);
    this.group.remove(d.group);
    d.dispose();
    if (subject && this.ward.timeline === subject && !this.wardSubjects().includes(subject))
      this.ward.setTimeline(undefined);
    this.ward.setOpen(this.wardSubjects());
  }

  private wardAction(
    dashboard: Dashboard,
    action: WardAction,
    subjectId: string,
    origin: THREE.Vector3,
  ): void {
    this.lastWardAction = `${action}:${subjectId}`;
    const invalidateProfiles = () => {
      for (const d of [...this.dashboards, ...this.pins.values(), ...this.wardPanels])
        if (d.id === 'PatientProfile') d.panel.invalidate();
    };
    switch (action) {
      case 'overlays': {
        const mode = this.ward.cycleOverlays();
        this.showNotice(
          [
            this.ward.overlayLabel(),
            mode === 'off' ? 'Overlays hidden.' : 'Shown on every mannequin in the ward.',
          ],
          2000,
        );
        invalidateProfiles();
        break;
      }
      case 'timeline': {
        const on = this.ward.timeline === subjectId;
        this.ward.setTimeline(on ? undefined : subjectId);
        if (!on) {
          // The discs lie in the ward; make sure a profile stands there too.
          if (!this.wardPanels.includes(dashboard))
            this.openWardProfile(subjectId, [...dashboard.path]);
          this.showNotice(
            ['Walk the timeline', `${subjectId}: step on a visit disc in the ward aisle.`],
            3000,
          );
        }
        invalidateProfiles();
        break;
      }
      case 'compare':
        if (dashboard.compareTo) {
          const other = dashboard.compareTo;
          if (this.wardPanels.includes(other)) this.closeWardPanel(other);
          else {
            dashboard.compareTo = undefined;
            other.compareTo = undefined;
            other.panel.invalidate();
          }
          dashboard.panel.invalidate();
        } else {
          this.comparePick = dashboard;
          this.showNotice(['Compare', 'Select another mannequin to pull alongside.'], 3000);
        }
        break;
      case 'table':
        this.togglePin(dashboard);
        break;
      case 'pull':
        this.toggleReview(subjectId);
        break;
      case 'filter':
        this.toggleDashboardsFor(subjectId);
        break;
      case 'go': {
        const stand = this.ward.standingPoint(subjectId);
        if (!stand) return;
        this.ctx.locomotion.teleport(stand.position);
        this.ctx.rig.faceTowards(stand.lookAt, this.ctx.inXr());
        this.openWardProfile(subjectId, [...dashboard.path]);
        this.showNotice([subjectId, 'You are in the ward, facing the mannequin.'], 2500);
        break;
      }
    }
    void origin;
  }

  /**
   * The profile's "Filter dashboards" switch: every dashboard (for the whole room) shows this
   * patient too, or no longer does. Several patients can be on at once.
   */
  toggleDashboardsFor(subjectId: string): void {
    const ids = this.roomFilters.filters.subjectIds ?? [];
    const on = ids.includes(subjectId);
    const next = on ? ids.filter((id) => id !== subjectId) : [...ids, subjectId];
    this.applyRoomFilter({ subjectIds: next.length ? next : undefined });
    this.showNotice(
      on
        ? [
            `Dashboards no longer filtered to ${subjectId}`,
            next.length
              ? `Still showing ${next.join(', ')}`
              : 'Every dashboard shows the whole study.',
          ]
        : [`Every dashboard shows ${next.join(', ')}`, 'Select the switch again to show everyone.'],
      3000,
    );
  }

  /** Grip on a mannequin: summon it for review, or send a summoned one back. */
  toggleReview(subjectId: string): void {
    if (this.ward.isPulled(subjectId)) this.sendBack(subjectId);
    else this.pullOut(subjectId);
  }

  /** A dragged cockpit: the subject's profile panel moves with it. */
  private followReview(subjectId: string): void {
    const panel = this.wardPanels.find(
      (d) => (d as PatientProfilesDashboard).currentSubject() === subjectId,
    );
    const pose =
      panel && this.ward.profilePose(subjectId, this.ctx.rig.headPosition(new THREE.Vector3()));
    if (!panel || !pose) return;
    panel.home.position.copy(pose.position);
    panel.home.quaternion.copy(pose.quaternion);
    panel.home.scale = pose.scale;
    panel.goHome();
  }

  private closeProfilesOf(subjectId: string): void {
    for (const d of [...this.wardPanels])
      if ((d as PatientProfilesDashboard).currentSubject() === subjectId) this.closeWardPanel(d);
  }

  /**
   * The mannequin comes to stand in front of the viewer with its record sheet beside it and
   * the profile opens on the other side. The dashboards are left alone (2026-10-06): the
   * profile's "Filter dashboards" switch narrows them (`toggleDashboardsFor`).
   */
  pullOut(subjectId: string): void {
    const head = this.ctx.rig.headPosition(new THREE.Vector3());
    const forward = this.ctx.rig.headForward(new THREE.Vector3());
    forward.y = 0;
    if (forward.lengthSq() < 1e-4) forward.set(0, 0, -1);
    forward.normalize();
    if (!this.ward.isPulled(subjectId) && this.ward.pulledIds.length >= REVIEW.maxPatients) {
      this.showNotice(
        [
          `${REVIEW.maxPatients} patients are out already`,
          'Grip one twice to send it back, then summon another.',
        ],
        3500,
      );
      return;
    }
    if (!this.ward.pullOut(subjectId, head, forward)) return;
    // The dashboards stay as they are; the profile's "Filter dashboards" switch narrows them.
    this.openWardProfile(subjectId, [`subject:${subjectId}`]);
    const n = this.ward.pulledIds.length;
    this.showNotice(
      n === 1
        ? [
            `${subjectId} summoned for review`,
            'Hold the grip to drag; grip twice to send back. "Filter dashboards" on the profile narrows every screen.',
          ]
        : [
            `${subjectId} summoned: ${n} patients under review`,
            'Turn around or drag a cockpit to compare.',
          ],
      4000,
    );
    this.ctx.audio.chime();
  }

  /**
   * One summoned patient back to the formation (or all of them): their cockpits fold away and
   * the review filter lists whoever is still out. With nobody left out, every dashboard
   * returns to its overview.
   */
  sendBack(subjectId?: string): void {
    const ids = this.ward.sendBack(subjectId);
    if (!ids.length) return;
    for (const id of ids) this.closeProfilesOf(id);
    const remaining = this.ward.pulledIds;
    // A patient going back takes their dashboard filter with them (its switch closes too).
    const filtered = this.roomFilters.filters.subjectIds ?? [];
    if (filtered.some((id) => ids.includes(id))) {
      const keep = filtered.filter((id) => !ids.includes(id));
      this.applyRoomFilter({ subjectIds: keep.length ? keep : undefined });
    }
    this.showNotice(
      [
        `${ids.join(', ')} sent back`,
        remaining.length
          ? `Still under review: ${remaining.join(', ')}`
          : 'Nobody is out for review.',
      ],
      2500,
    );
    this.ctx.audio.click();
  }

  private timelineStep(subjectId: string, visitId: string | undefined): void {
    if (!visitId) return;
    const panel = this.wardPanels.find(
      (d) => (d as PatientProfilesDashboard).currentSubject() === subjectId,
    );
    const path = [`subject:${subjectId}`, 'tab:Visits', `visit:${visitId}`];
    if (panel) panel.setPath(path);
    else this.openWardProfile(subjectId, path);
    this.ctx.audio.key();
  }

  /** Dev hook: select a mannequin (or one of its records) as a ray would. */
  wardSelectTarget(target: WardTarget): boolean {
    const at = this.ward.slotWorld(target.subjectId);
    if (!at) return false;
    this.wardSelect(target, at);
    return true;
  }

  /** Dev hook: select the n-th object of a dashboard's stage. */
  stageSelectIndex(id: DashboardId, index: number): boolean {
    const item = this.stages.items(id)[index];
    const at = item && this.stages.itemWorld(id, item);
    if (!item || !at) return false;
    this.stageSelect(id, item, at);
    return true;
  }

  /* ---------- Host callbacks ---------- */

  private showTooltip(lines: string[] | undefined, at: THREE.Vector3 | undefined): void {
    if (!lines || !at || this.manipulator.grabbing) {
      this.tooltip.hide();
      return;
    }
    this.tooltip.show(lines, at, this.ctx.rig.headPosition(new THREE.Vector3()));
  }

  showNotice(lines: string[], ms = 4000): void {
    this.notice.setLines(lines);
    this.notice.sprite.visible = true;
    this.noticeUntil = performance.now() + ms;
  }

  private raiseQuery(
    detail: RecordDetail,
    field: string | undefined,
    dashboard: DashboardId,
    origin: THREE.Vector3,
  ): void {
    const ref: RecordRef = { ...detail.ref };
    if (field) ref.field = field;
    this.lastRaise = {
      ref,
      field,
      dashboard,
      title: detail.title,
      currentValue: field ? detail.rows.find((r) => r.field === field)?.value : detail.currentValue,
      origin: origin.clone(),
    };
    this.card.hide();
    this.form.open(
      {
        ref,
        title: detail.title,
        currentValue: this.lastRaise.currentValue,
        dashboard,
        category: defaultCategory(dashboard, field),
        priority: 'Normal',
        text: '',
      },
      origin,
      this.ctx.rig.headPosition(new THREE.Vector3()),
    );
    this.ctx.audio.click();
  }

  private async submitQuery(input: ReturnType<typeof draftToInput>): Promise<void> {
    const q = await sendJson<Query>('POST', api('queries'), input).catch(rethrowApi);
    this.lastSubmitted = q;
    // The room broadcast will also deliver it; apply now so the sender never waits.
    this.onQuery(q, 'created');
    this.showNotice(
      [`Query raised on ${q.subjectId} · ${q.form}`, 'Everyone in the room can see the marker.'],
      3000,
    );
    this.ctx.audio.chime();
  }

  /* ---------- Markers and the card ---------- */

  private markerTooltip(id: string): string[] | undefined {
    const q = this.view.queries.find((x) => x.id === id);
    if (!q) return undefined;
    return [
      `${q.subjectId} · ${q.form}${q.field ? ` · ${q.field}` : ''}`,
      q.text.length > 70 ? `${q.text.slice(0, 67)}…` : q.text,
      `${q.status} · ${q.priority} · ${q.raisedBy.name}${q.responses.length ? ` · ${q.responses.length} response${q.responses.length === 1 ? '' : 's'}` : ''}`,
      'Select to open',
    ];
  }

  openQueryCard(id: string, at?: THREE.Vector3): boolean {
    const q = this.view.queries.find((x) => x.id === id);
    if (!q) return false;
    this.form.hide();
    const origin =
      at ??
      this.markers.positionOf(id) ??
      this.ctx.rig.headPosition(new THREE.Vector3()).add(new THREE.Vector3(0, -0.2, -1));
    this.card.open(q, origin, this.ctx.rig.headPosition(new THREE.Vector3()));
    return true;
  }

  private showQueryRecord(q: Query): void {
    const d = this.dashboard('QueryManagement');
    if (!d) return;
    const age = Math.floor((Date.now() - Date.parse(q.raisedAt)) / 86_400_000);
    const bucket = age <= 7 ? '0-7' : age <= 14 ? '8-14' : age <= 30 ? '15-30' : '30+';
    d.setPath([`age:${bucket}`, `query:${q.id}`]);
    d.flash(2000);
    this.lightFlash.set('QueryManagement', performance.now() + 2000);
    this.card.hide();
  }

  /** The table pin showing this subject's profile, if any. */
  private profilePin(subjectId: string): string | undefined {
    for (const [id, clone] of this.pins)
      if (clone instanceof PatientProfilesDashboard && clone.currentSubject() === subjectId)
        return id;
    return undefined;
  }

  private togglePin(dashboard: Dashboard): void {
    if (dashboard.pinId) {
      this.ctx.net.pinRemove(dashboard.pinId);
      return;
    }
    // A profile already on the table for this subject: pinning again removes it, never duplicates.
    if (dashboard instanceof PatientProfilesDashboard) {
      const subject = dashboard.currentSubject();
      const existing = subject && this.profilePin(subject);
      if (existing) {
        this.ctx.net.pinRemove(existing);
        this.showNotice([`${subject} taken off the table`], 2000);
        return;
      }
    }
    if (this.pins.size >= TABLE_PIN_SLOTS)
      this.showNotice(['The table is full', 'The oldest pin makes room.']);
    this.ctx.net.pinAdd(dashboard.id, {
      filters: { ...dashboard.filters },
      path: [...dashboard.path],
    });
    this.ctx.audio.click();
  }

  private broadcast(dashboard: Dashboard): void {
    this.ctx.net.broadcastView(dashboard.id, [...dashboard.path]);
    dashboard.flash(1500);
    this.showNotice(
      [`Broadcast: ${dashboard.title}`, 'Everyone in the room gets a pointer to it.'],
      2500,
    );
    this.ctx.audio.chime();
  }

  /* ---------- Room events ---------- */

  private setPins(pins: PinnedPanel[]): void {
    const keep = new Set(pins.map((p) => p.id));
    for (const id of [...this.pins.keys()]) if (!keep.has(id)) this.removePin(id);
    for (const pin of pins) this.addPin(pin);
  }

  private addPin(pin: PinnedPanel): void {
    if (this.pins.has(pin.id)) return;
    const clone = createDashboard(pin.dashboard, this.host);
    clone.pinId = pin.id;
    clone.panel.setResolution(1);
    clone.filters = { ...pin.state.filters };
    clone.path = [...pin.state.path];
    clone.invalidate();
    const angle = -Math.PI / 2 + (pin.slot / TABLE_PIN_SLOTS) * Math.PI * 2;
    const x = ROOM.tableCentre.x + Math.cos(angle) * PIN_RADIUS;
    const z = ROOM.tableCentre.z + Math.sin(angle) * PIN_RADIUS;
    clone.home.position.set(x, PIN_HEIGHT, z);
    const yaw = Math.atan2(x - ROOM.tableCentre.x, z - ROOM.tableCentre.z);
    clone.home.quaternion.setFromEuler(new THREE.Euler(-0.3, yaw, 0, 'YXZ'));
    clone.home.scale = PIN_SCALE;
    clone.goHome();
    this.group.add(clone.group);
    clone.attach();
    this.manipulator.add(clone);
    this.pins.set(pin.id, clone);
    // Drilling a pinned clone and moving it are shared like any screen.
    this.shareScreen(`pin:${pin.id}`, clone);
    this.refreshMiniatures();
    this.presentation.refreshPins();
    if (pin.pinnedBy.sessionId !== this.ctx.net.sessionId)
      this.showNotice([`${pin.pinnedBy.name} pinned ${clone.title}`, 'It is on the table.'], 3000);
  }

  private removePin(id: string): void {
    const clone = this.pins.get(id);
    if (!clone) return;
    this.pins.delete(id);
    this.sync.unregister(`pin:${id}`);
    this.sync.unregister(`place:pin:${id}`);
    this.targets.delete(clone);
    this.manipulator.remove(clone);
    this.group.remove(clone.group);
    clone.dispose();
    this.refreshMiniatures();
    this.presentation.refreshPins();
  }

  /** A miniature mannequin stands on the table in front of every pinned profile. */
  private refreshMiniatures(): void {
    const items = [];
    for (const [id, clone] of this.pins) {
      if (!(clone instanceof PatientProfilesDashboard)) continue;
      const subject = clone.currentSubject();
      if (!subject) continue;
      const dir = clone.home.position.clone().sub(ROOM.tableCentre).setY(0).normalize();
      const position = ROOM.tableCentre
        .clone()
        .addScaledVector(dir, PIN_RADIUS + 0.3)
        .setY(0.98);
      items.push({ id, subjectId: subject, position, yaw: Math.atan2(dir.x, dir.z) });
    }
    this.ward.setMiniatures(items);
  }

  /* ---------- The assistant ---------- */

  /** Flashes a dashboard and its light so the user can find it, with a notice. */
  highlightDashboard(id: DashboardId, by: string): void {
    const d = this.dashboard(id);
    if (!d) return;
    d.flash(6000);
    this.lightFlash.set(id, performance.now() + 6000);
    // The asker also gets it on the centre screen, facing the table.
    if (this.centre.isVisible) this.centre.show(id);
    this.showNotice(
      [`${by} ${d.title}`, 'It is on the centre screen; its panel light is flashing.'],
      4000,
    );
  }

  /** Opens the query form on the record the assistant suggested, with its text; the user sends. */
  raiseSuggested(s: AssistantSuggestion, origin: THREE.Vector3): void {
    const subject = this.view.subject(s.subjectId);
    if (!subject) return;
    const dashboard: DashboardId = /adverse/i.test(s.form)
      ? 'SAEReview'
      : /lab/i.test(s.form)
        ? 'LabAbnormality'
        : /visit/i.test(s.form)
          ? 'VisitCompliance'
          : /deviation/i.test(s.form)
            ? 'ProtocolDeviation'
            : 'PatientProfile';
    const ref: RecordRef = {
      study: this.view.code,
      subjectId: s.subjectId,
      form: s.form,
      recordId: s.recordId,
      ...(s.visit ? { visit: s.visit } : {}),
    };
    this.raiseQuery(
      { ref, title: `${s.form} · ${s.subjectId}`, rows: [] },
      s.field,
      dashboard,
      origin,
    );
    this.form.setText(s.text);
    if (s.category) {
      const category = QUERY_CATEGORIES.find((c) => c === s.category);
      if (category) this.form.setCategory(category);
    }
  }

  private onBroadcast(from: string, dashboard: DashboardId, path: string[]): void {
    this.lastBroadcast = { from, dashboard, path };
    const d = this.dashboard(dashboard);
    if (!d) return;
    d.flash(4000);
    this.lightFlash.set(dashboard, performance.now() + 4000);
    this.showNotice(
      [
        `${from} points at ${d.title}`,
        path.length
          ? `View: ${path.map((p) => p.replace(/^[a-z]+:/, '')).join(' › ')}`
          : 'Overview',
        'Look for the flashing panel light.',
      ],
      5000,
    );
    this.ctx.audio.chime();
  }

  private onQuery(q: Query, event: 'created' | 'updated'): void {
    if (q.study !== this.view.code) return;
    const before = this.view.queries.find((x) => x.id === q.id);
    this.view.upsertQuery(q);
    for (const d of this.dashboards) d.invalidate();
    for (const d of this.pins.values()) d.invalidate();
    this.centre.refresh();
    this.markers.set(this.view.queries, this.frames);
    this.card.refresh(q);
    this.ward.refreshData();
    this.lastQueryEvent = {
      id: q.id,
      event,
      status: q.status,
      responses: q.responses.length,
      receivedAt: Date.now(),
    };
    const mine = q.raisedBy.sessionId === this.ctx.net.sessionId;
    if (event === 'created' && !mine && !before) {
      this.showNotice(
        [
          `${q.raisedBy.name} raised a query`,
          `${q.subjectId} · ${q.form}${q.field ? ` · ${q.field}` : ''}`,
          q.text.length > 60 ? `${q.text.slice(0, 57)}…` : q.text,
        ],
        5000,
      );
      this.ctx.audio.chime();
    } else if (event === 'updated' && before) {
      const newResponse =
        q.responses.length > before.responses.length
          ? q.responses[q.responses.length - 1]
          : undefined;
      if (newResponse && newResponse.by !== this.ctx.profile.name) {
        this.showNotice(
          [
            `${newResponse.by} responded on ${q.subjectId}`,
            newResponse.text.length > 60 ? `${newResponse.text.slice(0, 57)}…` : newResponse.text,
            `Query is now ${q.status}`,
          ],
          5000,
        );
        this.ctx.audio.chime();
      } else if (
        q.status !== before.status &&
        q.closedBy &&
        q.closedBy.name !== this.ctx.profile.name
      ) {
        this.showNotice(
          [`Query on ${q.subjectId} ${q.status.toLowerCase()} by ${q.closedBy.name}`],
          4000,
        );
      }
    }
  }
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

/** A pose as the room shares it: millimetres and thousandths. */
function poseOf(p: THREE.Vector3, q: THREE.Quaternion, s: number): Place {
  return {
    p: [round3(p.x), round3(p.y), round3(p.z)],
    q: [round3(q.x), round3(q.y), round3(q.z), round3(q.w)],
    s: round3(s),
  };
}

function rethrowApi(err: unknown): never {
  if (err instanceof ApiError) {
    const fields = err.fields ? Object.values(err.fields).join('; ') : '';
    throw new Error(fields || err.message);
  }
  throw err instanceof Error ? err : new Error(String(err));
}

export function describeFilters(f: DashboardFilters, view: StudyView): string {
  const parts: string[] = [];
  if (f.subjectId) parts.push(`subject ${f.subjectId}`);
  if (f.subjectIds?.length) parts.push(`under review ${f.subjectIds.join(', ')}`);
  if (f.sex) parts.push(f.sex === 'F' ? 'female' : 'male');
  if (f.status) parts.push(f.status);
  if (f.flag) parts.push(f.flag);
  if (f.ageBand) parts.push(`age ${f.ageBand}`);
  if (f.bmiBucket) parts.push(`BMI ${f.bmiBucket.toLowerCase()}`);
  if (f.siteId) parts.push(view.siteLabel(f.siteId));
  if (f.arm) parts.push(f.arm);
  if (f.visitFrom || f.visitTo)
    parts.push(`visits ${f.visitFrom ?? 'start'} to ${f.visitTo ?? 'end'}`);
  if (f.dateFrom || f.dateTo) parts.push(`${f.dateFrom ?? '…'} to ${f.dateTo ?? '…'}`);
  return parts.join(' · ') || 'All data';
}
